import styles from '../marketplace.module.css'

import {Box, Heading, Label, Octicon, Text} from '@primer/react'
import {PlayIcon, VerifiedIcon} from '@primer/octicons-react'
import {SafeHTMLBox, type SafeHTMLString} from '@github-ui/safe-html'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useLocation} from 'react-router-dom'
import type {Listing} from '../types'
import {useMemo} from 'react'

interface FeaturedItemProps {
  listing: Listing
  isFeatured: boolean
}

export default function FeaturedItem(props: FeaturedItemProps) {
  const {listing, isFeatured} = props
  const description = listing.type === 'marketplace_listing' ? listing.shortDescription : listing.description
  const backgroundColor =
    listing.type === 'marketplace_listing' ? listing.bgColor : listing.repository_action.repository_action.color
  const listingUrl =
    listing.type === 'marketplace_listing' ? `marketplace/${listing.slug}` : `marketplace/actions/${listing.slug}`
  const isVerifiedOwner = listing.type === 'marketplace_listing' ? listing.isVerifiedOwner : listing.is_verified_owner

  const listingType = useMemo(() => {
    if (listing.type === 'repository_action') {
      return 'Action'
    } else if (listing.copilotApp) {
      return 'Copilot'
    } else {
      return 'App'
    }
  }, [listing])

  const location = useLocation()

  const sendClickEvent = () => {
    if (listing.type === 'marketplace_listing') {
      sendEvent('marketplace_listing_click', {
        marketplace_listing_id: listing.id,
        source_url: location.pathname,
        destination_url: listingUrl,
      })
    }
  }

  return (
    <div
      className={`position-relative border rounded-2 d-flex ${styles['marketplace-item']} ${
        isFeatured ? 'flex-column flex-items-center p-4' : 'gap-3 p-3'
      }`}
      data-testid="marketplace-item"
    >
      <Box
        data-testid="logo"
        className={`flex-shrink-0 rounded-3 overflow-hidden ${styles['marketplace-logo']}`}
        sx={{
          backgroundColor: `#${backgroundColor}`,
          color: backgroundColor === 'ffffff' ? 'var(--fgColor-black)' : 'var(--fgColor-white)',
        }}
      >
        {listing.type === 'marketplace_listing' ? (
          <img src={listing.listingLogoUrl} alt={`${listing.name} logo`} className={styles['marketplace-logo-img']} />
        ) : listing.icon_svg ? (
          <SafeHTMLBox html={listing.icon_svg as SafeHTMLString} className={styles['marketplace-logo-svg']} />
        ) : (
          <PlayIcon className={styles['marketplace-logo-svg']} />
        )}
      </Box>
      {isFeatured ? (
        <div
          className="d-flex flex-column flex-items-center height-full width-full text-center"
          data-testid="featured-item"
        >
          <Heading as="h3" className="mt-3 d-flex f4 lh-condensed">
            <a
              href={listingUrl}
              className={`fgColor-default line-clamp-1 ${styles['marketplace-item-link']}`}
              onClick={sendClickEvent}
            >
              {listing.name}
            </a>
            {isVerifiedOwner && (
              <Octicon icon={VerifiedIcon} className="fgColor-accent" sx={{ml: 2, transform: 'translateY(2px)'}} />
            )}
          </Heading>
          <Text as="p" className="mt-2 mb-auto height-full text-small fgColor-muted line-clamp-2">
            {description}
          </Text>
          <Label
            variant="secondary"
            size="large"
            data-testid="listing-type-label"
            className="position-absolute"
            sx={{top: '1rem', right: '1rem'}}
          >
            {listingType}
          </Label>
        </div>
      ) : (
        <div className="flex-1" data-testid="non-featured-item">
          <div className="d-flex flex-justify-between flex-items-start gap-3">
            <Heading as="h3" className="d-flex f4 lh-condensed">
              <a
                href={listingUrl}
                className={`${styles['marketplace-item-link']} line-clamp-1`}
                onClick={sendClickEvent}
              >
                {listing.name}
              </a>
              {isVerifiedOwner && (
                <Octicon icon={VerifiedIcon} className="fgColor-accent" sx={{ml: 2, transform: 'translateY(2px)'}} />
              )}
            </Heading>
            <Label variant="secondary" data-testid="listing-type-label">
              {listingType}
            </Label>
          </div>
          <Text as="p" className="mt-1 mb-0 text-small fgColor-muted line-clamp-2">
            {description}
          </Text>
        </div>
      )}
    </div>
  )
}

try{ FeaturedItem.displayName ||= 'FeaturedItem' } catch {}